import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { getApiErrorMessage } from '../../utils/apiHelpers';
import { Search, RefreshCw, Copy, FileJson, CheckCircle } from 'lucide-react';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

/**
 * UUID Resolver Tool Component
 * 
 * A tool for super admins to resolve UUIDs in database documents
 * to human-readable names (e.g., client_id -> "John Doe").
 * All usage is logged for audit purposes.
 * 
 * @param {boolean} open - Controls dialog visibility
 * @param {function} onOpenChange - Callback when dialog open state changes
 */
export const UUIDResolver = ({ open, onOpenChange }) => {
  const [jsonInput, setJsonInput] = useState('');
  const [resolvedResult, setResolvedResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleResolve = async () => {
    if (!jsonInput.trim()) {
      toast.error('Please paste a JSON document');
      return;
    }
    
    setLoading(true);
    setError(null);
    setResolvedResult(null);
    
    try {
      // Parse input as JSON
      let dataToResolve;
      try {
        dataToResolve = JSON.parse(jsonInput);
      } catch (parseError) {
        toast.error('Invalid JSON. Please check your input.');
        setError('Invalid JSON format. Please paste a valid JSON document.');
        setLoading(false);
        return;
      }
      
      const response = await axios.post(`${API}/admin/tools/resolve-uuids`, {
        data: dataToResolve
      }, {
        headers: {
          Authorization: `Bearer ${sessionStorage.getItem('token')}`
        }
      });
      
      setResolvedResult(response.data);
      
      if (response.data.uuids_resolved > 0) {
        toast.success(`Resolved ${response.data.uuids_resolved} UUID(s) to human-readable names`);
      } else {
        toast.info('No UUIDs found to resolve in this document');
      }
    } catch (error) {
      console.error('UUID resolution failed:', error);
      setError(getApiErrorMessage(error, 'UUID resolution failed'));
      toast.error('UUID resolution failed');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    onOpenChange(false);
    setJsonInput('');
    setResolvedResult(null);
    setError(null);
  };

  const handleCopyResult = () => {
    navigator.clipboard.writeText(JSON.stringify(resolvedResult.resolved_data, null, 2));
    toast.success('Copied to clipboard');
  };

  const handleFormatInput = () => {
    try {
      const parsed = JSON.parse(jsonInput);
      setJsonInput(JSON.stringify(parsed, null, 2));
      toast.success('JSON formatted');
    } catch {
      toast.error('Cannot format - invalid JSON');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-blue-900">
            <FileJson className="h-5 w-5" />
            UUID Resolver Tool
          </DialogTitle>
          <DialogDescription>
            Paste a JSON document from the database to resolve UUIDs (like client_id, provider_id) into human-readable names.
          </DialogDescription>
        </DialogHeader>
        
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
          <p className="text-sm text-blue-800 font-medium">Supported UUID Fields</p>
          <p className="text-xs text-blue-700 mt-1">
            client_id, provider_id, created_by, locked_by, template_id, supervisor_signed_by, provider_signed_by, assigned_supervisor, and more.
          </p>
        </div>
        
        <div className="space-y-4">
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label>JSON Document</Label>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleFormatInput}
                className="text-xs h-7"
                disabled={!jsonInput.trim()}
              >
                Format JSON
              </Button>
            </div>
            <p className="text-xs text-gray-500 mb-2">
              Paste a document copied from MongoDB or your database
            </p>
            <textarea
              className="w-full h-48 p-3 border rounded-lg font-mono text-sm bg-gray-50"
              placeholder='{\n  "id": "abc-123",\n  "client_id": "uuid-of-client",\n  "provider_id": "uuid-of-provider",\n  "created_at": "2026-01-01T00:00:00Z"\n}'
              value={jsonInput}
              onChange={(e) => setJsonInput(e.target.value)}
              spellCheck={false}
            />
          </div>
          
          <Button 
            onClick={handleResolve} 
            disabled={loading || !jsonInput.trim()}
            className="w-full bg-blue-600 hover:bg-blue-700"
          >
            {loading ? (
              <>
                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                Resolving UUIDs...
              </>
            ) : (
              <>
                <Search className="h-4 w-4 mr-2" />
                Resolve UUIDs
              </>
            )}
          </Button>
          
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <p className="text-sm text-red-800 font-medium">Error</p>
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}
          
          {resolvedResult && (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label className={resolvedResult.uuids_resolved > 0 ? "text-green-800" : "text-gray-800"}>
                  {resolvedResult.uuids_resolved > 0 ? "Resolved Document" : "Result (No UUIDs Found)"}
                </Label>
                <Badge className={resolvedResult.uuids_resolved > 0 ? "bg-green-100 text-green-800" : "bg-gray-100 text-gray-800"}>
                  {resolvedResult.uuids_resolved} UUID(s) resolved
                </Badge>
              </div>
              
              {/* Resolution Log */}
              {resolvedResult.resolution_log && resolvedResult.resolution_log.length > 0 && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                  <p className="text-sm text-green-800 font-medium mb-2">Resolved Fields:</p>
                  <ul className="space-y-1">
                    {resolvedResult.resolution_log.map((item, idx) => (
                      <li key={idx} className="text-xs text-green-700 flex items-center gap-1">
                        <CheckCircle className="h-3 w-3" />
                        <span className="font-mono">{item.field}</span>
                        <span className="text-green-500">→</span>
                        <span className="font-medium">{item.resolved_to}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              
              {/* Resolved Data */}
              <div className={`${resolvedResult.uuids_resolved > 0 ? "bg-green-50 border-green-200" : "bg-gray-50 border-gray-200"} border rounded-lg p-3`}>
                <pre className="whitespace-pre-wrap text-sm font-mono text-gray-900 overflow-x-auto max-h-64 overflow-y-auto">
                  {JSON.stringify(resolvedResult.resolved_data, null, 2)}
                </pre>
              </div>
              
              <Button
                variant="outline"
                size="sm"
                onClick={handleCopyResult}
              >
                <Copy className="h-4 w-4 mr-1" />
                Copy Resolved Document
              </Button>
            </div>
          )}
        </div>
        
        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={handleClose}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default UUIDResolver;
